import React, { useState, useEffect } from 'react';
import axios from '../api/axios';
import { useUser } from '../context/UserContext';
import { getAssignableRoles } from '../utils/permissions';

export default function AdminStaffApplicationPage() {
  const { user } = useUser();
  
  // View mode: 'periods' or 'applications'
  const [viewMode, setViewMode] = useState('periods');
  const [selectedPeriod, setSelectedPeriod] = useState(null);
  
  // Applications state
  const [applications, setApplications] = useState([]);
  const [applicationsLoading, setApplicationsLoading] = useState(true);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [filter, setFilter] = useState('ALL');
  const [semesterFilter, setSemesterFilter] = useState('ALL');
  const [branchFilter, setBranchFilter] = useState('ALL');
  
  // Assign Role Modal state
  const [assignModal, setAssignModal] = useState({ open: false, userId: null, email: '', currentRole: null, applicationBranch: '' });
  const [assignRole, setAssignRole] = useState('STAFF');
  const [assignBranch, setAssignBranch] = useState('TAIPEI');

  // Recruitments state
  const [recruitments, setRecruitments] = useState([]);
  const [recruitmentsLoading, setRecruitmentsLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingRecruitment, setEditingRecruitment] = useState(null);
  const [recruitmentError, setRecruitmentError] = useState('');

  const [recruitmentFormData, setRecruitmentFormData] = useState({
    branch: 'NCCU',
    semester: '',
    startDate: '',
    endDate: '',
    isActive: true
  });

  const branches = ['NCCU', 'NTU', 'TAIPEI'];

  useEffect(() => {
    fetchApplications();
    fetchRecruitments();
    window.scrollTo(0, 0);
  }, []);

  const fetchApplications = async () => {
    try {
      const response = await axios.get('/api/staff-application/admin/all');
      setApplications(response.data);
    } catch (error) {
      alert('Failed to fetch applications');
    } finally {
      setApplicationsLoading(false);
    }
  };

  const fetchRecruitments = async () => {
    try {
      const response = await axios.get('/api/recruitment/admin/all');
      setRecruitments(response.data);
    } catch (error) {
      alert('Failed to fetch recruitments');
    } finally {
      setRecruitmentsLoading(false);
    }
  };

  const handlePeriodClick = (recruitment) => {
    setSelectedPeriod(recruitment);
    setViewMode('applications');
    // Filter applications by this period
    setBranchFilter(recruitment.branch);
    setSemesterFilter(recruitment.semester);
  };

  const handleApprove = async (applicationId) => {
    if (!window.confirm('Are you sure you want to approve this application?')) {
      return;
    }

    try {
      await axios.post('/api/staff-application/admin/approve', null, {
        params: { applicationId }
      });
      alert('Application approved successfully!');
      fetchApplications();
    } catch (error) {
      alert('Failed to approve application');
    }
  };

  const handleReject = async (applicationId) => {
    if (!window.confirm('Are you sure you want to reject this application?')) {
      return;
    }

    try {
      await axios.post('/api/staff-application/admin/reject', null, {
        params: { applicationId }
      });
      alert('Application rejected successfully!');
      fetchApplications();
    } catch (error) {
      alert('Failed to reject application');
    }
  };

  const handleCreateRecruitment = async (e) => {
    e.preventDefault();
    setRecruitmentError('');

    try {
      await axios.post('/api/recruitment/admin/create', recruitmentFormData);
      alert('Recruitment period created successfully!');
      setShowCreateModal(false);
      resetRecruitmentForm();
      fetchRecruitments();
    } catch (error) {
      setRecruitmentError(error.response?.data || 'Failed to create recruitment');
    }
  };

  const handleUpdateRecruitment = async (e) => {
    e.preventDefault();
    setRecruitmentError('');

    try {
      await axios.put(`/api/recruitment/admin/${editingRecruitment.id}`, recruitmentFormData);
      alert('Recruitment period updated successfully!');
      setEditingRecruitment(null);
      resetRecruitmentForm();
      fetchRecruitments();
    } catch (error) {
      setRecruitmentError(error.response?.data || 'Failed to update recruitment');
    }
  };

  const handleDeleteRecruitment = async (id) => {
    if (!window.confirm('Are you sure you want to delete this recruitment period? This cannot be undone.')) {
      return;
    }

    try {
      await axios.delete(`/api/recruitment/admin/${id}`);
      alert('Recruitment period deleted successfully!');
      fetchRecruitments();
    } catch (error) {
      alert(error.response?.data || 'Failed to delete recruitment');
    }
  };

  const handleEditRecruitment = (recruitment) => {
    setEditingRecruitment(recruitment);
    setRecruitmentFormData({
      branch: recruitment.branch,
      semester: recruitment.semester,
      startDate: recruitment.startDate,
      endDate: recruitment.endDate,
      isActive: recruitment.isActive
    });
  };

  const resetRecruitmentForm = () => {
    setRecruitmentFormData({
      branch: 'NCCU',
      semester: '',
      startDate: '',
      endDate: '',
      isActive: true
    });
    setRecruitmentError('');
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'PENDING':
        return 'bg-yellow-100 text-yellow-800';
      case 'APPROVED':
        return 'bg-green-100 text-green-800';
      case 'REJECTED':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  
  // Handle opening Assign Role modal
  const handleAssignRole = (application) => {
    setAssignModal({ 
      open: true, 
      userId: application.userId, 
      email: application.email, 
      currentRole: 'GUEST', // Applicants are GUEST
      applicationBranch: application.branch 
    });
    setAssignRole('STAFF');
    setAssignBranch(application.branch || 'TAIPEI');
  };
  
  // Handle submit of Assign Role
  const submitAssign = async () => {
    try {
      const response = await axios.post('/api/admin/users/assign-staff', {
        userId: assignModal.userId,
        targetRole: assignRole,
        reason: `Assigned as ${assignRole} via staff application approval`,
        branch: assignBranch
      });
      
      if (response.data.success) {
        alert(response.data.message);
        setAssignModal({ open: false, userId: null, email: '', currentRole: null, applicationBranch: '' });
        fetchApplications(); // Refresh the list
      } else {
        alert(response.data.message);
      }
    } catch (error) {
      alert('Failed to assign role: ' + (error.response?.data?.message || error.message));
    }
  };

  const getRecruitmentStatusBadge = (recruitment) => {
    if (recruitment.isCurrentlyOpen) {
      return <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-medium">Open</span>;
    } else if (recruitment.isActive) {
      return <span className="px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-sm font-medium">Upcoming</span>;
    } else {
      return <span className="px-3 py-1 bg-gray-100 text-gray-800 rounded-full text-sm font-medium">Closed</span>;
    }
  };

  const getApplicationsCountForPeriod = (recruitment) => {
    return applications.filter(app => 
      app.branch === recruitment.branch && 
      app.recruitmentPeriod === recruitment.semester
    ).length;
  };

  const uniqueSemesters = [...new Set(applications.map(app => app.recruitmentPeriod).filter(Boolean))].sort().reverse();
  const uniqueBranches = [...new Set(applications.map(app => app.branch).filter(Boolean))].sort();
  
  // Get applications for the selected period only
  const periodApplications = applications.filter(app => {
    if (!selectedPeriod) return false;
    return app.branch === selectedPeriod.branch && app.recruitmentPeriod === selectedPeriod.semester;
  });
  
  const filteredApplications = periodApplications.filter(app => {
    const statusMatch = filter === 'ALL' || app.status === filter;
    return statusMatch;
  });

  if (applicationsLoading || recruitmentsLoading) {
    return <div className="p-8">Loading...</div>;
  }

  return (
    <div className="p-8">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold">Staff Recruitment Management</h1>
        <div className="flex space-x-3">
          {viewMode === 'periods' ? (
            <button
              onClick={() => setShowCreateModal(true)}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              + Create Period
            </button>
          ) : (
            <button
              onClick={() => {
                setViewMode('periods');
                setSelectedPeriod(null);
                setBranchFilter('ALL');
                setSemesterFilter('ALL');
              }}
              className="px-6 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
            >
              ← Back to Periods
            </button>
          )}
        </div>
      </div>

      {/* Recruitment Periods View */}
      {viewMode === 'periods' && (
        <>
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-l-4 border-blue-500 p-6 mb-6 rounded-lg shadow-sm">
            <h2 className="text-lg font-bold text-blue-900 mb-4">📋 Semester Staff Management Checklist</h2>
            <div className="space-y-3 text-sm text-blue-800">
              <div className="flex items-start">
                <span className="font-bold mr-2">1.</span>
                <p>Ask all current staff members if they want to continue for the next semester.</p>
              </div>
              <div className="flex items-start">
                <span className="font-bold mr-2">2.</span>
                <p>Change those who are leaving to <strong>GUEST</strong> role. (Go to Member Info → Action → Assign Role)</p>
              </div>
              <div className="flex items-start">
                <span className="font-bold mr-2">3.</span>
                <p>Promote new <strong>LEADER</strong> if needed. (Go to Member Info → Action → Assign Role)</p>
              </div>
              <div className="flex items-start">
                <span className="font-bold mr-2">4.</span>
                <p className="text-red-700"><strong>⚠️ Important:</strong> Each branch can only have <strong>ONE PRESIDENT</strong>. When appointing a new President, the existing President in that branch will automatically be changed to <strong>STAFF</strong> (not GUEST). Please be careful when assigning President roles.</p>
              </div>
              <div className="flex items-start">
                <span className="font-bold mr-2">Note:</span>
                <p>If the previous <strong>PRESIDENT</strong> wants to leave completely, they should first appoint a new PRESIDENT, and then change themselves to <strong>GUEST</strong> role to fully leave.</p>
              </div>
            </div>
          </div>

          <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 mb-6 rounded">
            <div className="space-y-2 text-sm text-yellow-800">
              <p><strong>📌 Reminder:</strong> After completing steps 1-4 above, create a new recruitment period for each branch using the <strong>"+ Create Period"</strong> button above.</p>
              <p><strong>💡 How to Review:</strong> Click on a created recruitment period card below to view all applications for that semester, then Approve or Reject each application.</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {recruitments.length === 0 ? (
              <div className="col-span-full text-center py-12 bg-gray-50 rounded-lg">
                <p className="text-gray-500">No recruitment periods configured yet</p>
              </div>
            ) : (
              recruitments.map((recruitment) => {
                const appsCount = getApplicationsCountForPeriod(recruitment);
                return (
                  <div
                    key={recruitment.id}
                    className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-all cursor-pointer border-2 border-transparent hover:border-blue-400"
                    onClick={() => handlePeriodClick(recruitment)}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-xl font-bold">{recruitment.branch}</h3>
                      {getRecruitmentStatusBadge(recruitment)}
                    </div>
                    <div className="mb-3">
                      <span className="px-3 py-1 bg-indigo-100 text-indigo-800 rounded-full text-sm font-medium">
                        {recruitment.semester}
                      </span>
                    </div>
                    <div className="space-y-1 text-sm text-gray-600 mb-4">
                      <p><strong>Start:</strong> {new Date(recruitment.startDate).toLocaleDateString()}</p>
                      <p><strong>End:</strong> {new Date(recruitment.endDate).toLocaleDateString()}</p>
                    </div>
                    <div className="flex items-center justify-between pt-3 border-t">
                      <span className="text-lg font-bold text-blue-600">
                        {appsCount} Application{appsCount !== 1 ? 's' : ''}
                      </span>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditRecruitment(recruitment);
                        }}
                        className="px-3 py-1 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                      >
                        Edit
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteRecruitment(recruitment.id);
                        }}
                        className="px-3 py-1 text-red-600 hover:bg-red-50 rounded transition-colors"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </>
      )}

      {/* Applications View */}
      {viewMode === 'applications' && selectedPeriod && (
        <>
          {/* Selected Period Info */}
          <div className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white p-6 rounded-lg mb-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold mb-2">{selectedPeriod.branch} - {selectedPeriod.semester}</h2>
                <p className="text-blue-100">
                  {new Date(selectedPeriod.startDate).toLocaleDateString()} ~ {new Date(selectedPeriod.endDate).toLocaleDateString()}
                </p>
              </div>
              {getRecruitmentStatusBadge(selectedPeriod)}
            </div>
          </div>

          {/* Filter Tabs */}
          <div className="flex space-x-4 mb-6 border-b">
            {['ALL', 'PENDING', 'APPROVED', 'REJECTED'].map((status) => {
              const count = periodApplications.filter(app => status === 'ALL' ? true : app.status === status).length;
              return (
                <button
                  key={status}
                  onClick={() => setFilter(status)}
                  className={`px-4 py-2 font-medium border-b-2 transition-colors ${
                    filter === status
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {status} ({count})
                </button>
              );
            })}
          </div>

          {/* Applications List */}
          <div className="space-y-4">
            {filteredApplications.length === 0 ? (
              <div className="text-center py-12 bg-gray-50 rounded-lg">
                <p className="text-gray-500">No applications found for this period</p>
              </div>
            ) : (
              filteredApplications.map((app) => (
                <div
                  key={app.id}
                  className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow cursor-pointer"
                  onClick={() => setSelectedApplication(app)}
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center space-x-4 mb-2">
                        <h3 className="text-xl font-bold">{app.name}</h3>
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(app.status)}`}>
                          {app.status}
                        </span>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm text-gray-600">
                        <p><strong>Email:</strong> {app.email}</p>
                        <p><strong>Student ID:</strong> {app.studentId}</p>
                        <p><strong>Major:</strong> {app.major}</p>
                        <p><strong>MBTI:</strong> {app.mbti}</p>
                        <p><strong>1st Priority:</strong> {app.position1stPriority}</p>
                        <p><strong>Applied:</strong> {new Date(app.createdAt).toLocaleDateString()}</p>
                      </div>
                    </div>
                    <div className="flex space-x-2">
                      {app.status === 'PENDING' && (
                        <>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleApprove(app.id);
                            }}
                            className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
                          >
                            Approve
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleReject(app.id);
                            }}
                            className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                          >
                            Reject
                          </button>
                        </>
                      )}
                      {app.status === 'APPROVED' && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleAssignRole(app);
                          }}
                          className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                        >
                          Assign Role
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </>
      )}

      {/* Application Detail Modal */}
      {selectedApplication && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto p-8">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold">{selectedApplication.name} - Application Details</h2>
              <button
                onClick={() => setSelectedApplication(null)}
                className="text-gray-400 hover:text-gray-600 text-3xl font-bold"
              >
                ×
              </button>
            </div>

            <div className="space-y-6">
              <section>
                <h3 className="text-lg font-bold mb-3 pb-2 border-b">Basic Information</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div><strong>Name:</strong> {selectedApplication.name}</div>
                  <div><strong>Student ID:</strong> {selectedApplication.studentId}</div>
                  <div><strong>Email:</strong> {selectedApplication.email}</div>
                  <div><strong>Branch:</strong> {selectedApplication.branch || 'N/A'}</div>
                  <div><strong>Major:</strong> {selectedApplication.major} / Year {selectedApplication.year}</div>
                  <div><strong>Semester:</strong> {selectedApplication.recruitmentPeriod || 'N/A'}</div>
                  <div><strong>Line ID:</strong> {selectedApplication.lineId || 'N/A'}</div>
                  <div><strong>Instagram ID:</strong> {selectedApplication.instagramId}</div>
                  <div><strong>MBTI:</strong> {selectedApplication.mbti}</div>
                  <div><strong>Applied:</strong> {new Date(selectedApplication.createdAt).toLocaleString()}</div>
                </div>
              </section>

              <section>
                <h3 className="text-lg font-bold mb-3 pb-2 border-b">Position Priorities</h3>
                <div className="space-y-2 text-sm">
                  <p><strong>1st:</strong> {selectedApplication.position1stPriority}</p>
                  <p><strong>2nd:</strong> {selectedApplication.position2ndPriority || 'N/A'}</p>
                  <p><strong>3rd:</strong> {selectedApplication.position3rdPriority || 'N/A'}</p>
                </div>
              </section>

              <section>
                <h3 className="text-lg font-bold mb-3 pb-2 border-b">Motivation</h3>
                <p className="text-sm whitespace-pre-wrap">{selectedApplication.motivation}</p>
              </section>

              <section>
                <h3 className="text-lg font-bold mb-3 pb-2 border-b">Development Goal</h3>
                <p className="text-sm whitespace-pre-wrap">{selectedApplication.developmentGoal}</p>
              </section>

              <section>
                <h3 className="text-lg font-bold mb-3 pb-2 border-b">Portfolio</h3>
                <div className="space-y-2 text-sm">
                  <p><strong>Has Portfolio:</strong> {selectedApplication.hasPortfolio ? 'Yes' : 'No'}</p>
                  <p><strong>Link:</strong> <a href={selectedApplication.portfolioLink} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">{selectedApplication.portfolioLink}</a></p>
                </div>
              </section>

              <section>
                <h3 className="text-lg font-bold mb-3 pb-2 border-b">Recruitment Bonus</h3>
                <div className="space-y-2 text-sm">
                  <p><strong>EXPO IG Story Share:</strong> {selectedApplication.hasRecruitmentBonus ? 'YES, I have shared' : "NO, I haven't"}</p>
                </div>
              </section>

              <section>
                <h3 className="text-lg font-bold mb-3 pb-2 border-b">Commitment</h3>
                <p className="text-sm"><strong>Agrees with commitments:</strong> {selectedApplication.agreesWithCommitment ? 'Yes' : 'No'}</p>
              </section>
            </div>

            <div className="mt-8 flex justify-end space-x-4">
              <button
                onClick={() => setSelectedApplication(null)}
                className="px-6 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
              >
                Close
              </button>
              {selectedApplication.status === 'PENDING' && (
                <>
                  <button
                    onClick={() => {
                      handleApprove(selectedApplication.id);
                      setSelectedApplication(null);
                    }}
                    className="px-6 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
                  >
                    Approve
                  </button>
                  <button
                    onClick={() => {
                      handleReject(selectedApplication.id);
                      setSelectedApplication(null);
                    }}
                    className="px-6 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                  >
                    Reject
                  </button>
                </>
              )}
              {selectedApplication.status === 'APPROVED' && (
                <button
                  onClick={() => {
                    handleAssignRole(selectedApplication);
                    setSelectedApplication(null);
                  }}
                  className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                >
                  Assign Role
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Assign Role Modal */}
      {assignModal.open && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">Assign Staff</h2>
            <p className="text-sm text-gray-600 mb-4">
              Assign {assignModal.email} as staff. An onboarding email will be sent.
            </p>
            
            {/* Branch selection */}
            <div>
              <label className="block text-sm mb-1 font-medium">Branch</label>
              <select 
                value={assignBranch} 
                onChange={e => setAssignBranch(e.target.value)} 
                className="w-full border rounded p-2 mb-4"
              >
                <option value="TAIPEI">TAIPEI</option>
                <option value="NCCU">NCCU</option>
                <option value="NTU">NTU</option>
              </select>
            </div>
            
            {/* Role selection */}
            <div>
              <label className="block text-sm mb-1 font-medium">New Role</label>
              <select 
                value={assignRole} 
                onChange={e => setAssignRole(e.target.value)} 
                className="w-full border rounded p-2 mb-6"
              >
                {getAssignableRoles(user?.role, assignModal.currentRole).map(role => (
                  <option key={role} value={role}>{role}</option>
                ))}
              </select>
            </div>
            
            <div className="flex justify-end gap-3">
              <button 
                onClick={() => setAssignModal({ open: false, userId: null, email: '', currentRole: null, applicationBranch: '' })} 
                className="px-4 py-2 bg-gray-200 rounded"
              >
                Cancel
              </button>
              <button 
                onClick={submitAssign} 
                className="px-4 py-2 bg-blue-600 text-white rounded"
              >
                Send Invitation
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Create/Edit Recruitment Modal */}
      {(showCreateModal || editingRecruitment) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-2xl p-8">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold">
                {editingRecruitment ? 'Edit Recruitment Period' : 'Create Recruitment Period'}
              </h2>
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setEditingRecruitment(null);
                  resetRecruitmentForm();
                }}
                className="text-gray-400 hover:text-gray-600 text-3xl font-bold"
              >
                ×
              </button>
            </div>

            {recruitmentError && (
              <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 rounded mb-4">
                {recruitmentError}
              </div>
            )}

            <form onSubmit={editingRecruitment ? handleUpdateRecruitment : handleCreateRecruitment} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Branch *
                </label>
                <select
                  value={recruitmentFormData.branch}
                  onChange={(e) => setRecruitmentFormData({ ...recruitmentFormData, branch: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                >
                  {branches.map(branch => (
                    <option key={branch} value={branch}>{branch}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Semester *
                </label>
                <input
                  type="text"
                  value={recruitmentFormData.semester}
                  onChange={(e) => setRecruitmentFormData({ ...recruitmentFormData, semester: e.target.value })}
                  placeholder="e.g., 2025 Spring, 2025-1"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Start Date *
                  </label>
                  <input
                    type="date"
                    value={recruitmentFormData.startDate}
                    onChange={(e) => setRecruitmentFormData({ ...recruitmentFormData, startDate: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    End Date *
                  </label>
                  <input
                    type="date"
                    value={recruitmentFormData.endDate}
                    onChange={(e) => setRecruitmentFormData({ ...recruitmentFormData, endDate: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={recruitmentFormData.isActive}
                    onChange={(e) => setRecruitmentFormData({ ...recruitmentFormData, isActive: e.target.checked })}
                    className="mr-2"
                  />
                  <span className="text-sm text-gray-700">Active</span>
                </label>
              </div>

              <div className="flex justify-end space-x-4 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false);
                    setEditingRecruitment(null);
                    resetRecruitmentForm();
                  }}
                  className="px-6 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  {editingRecruitment ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
